import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';

import { InputAdornment, TextField } from '@mui/material';
import { IconButtonRounded, NgCard, NgCardContent } from '../UI';
import { setQuery, viewStateSelectors } from '../../store';
import { config } from '../../config';

const StyledSearchContainer = styled.div`
  .MuiTextField-root {
    width: 100%;
  }
`;

interface Props {
  viewStateKey: string;
  noWrapper?: boolean;
}

export const NgFilters: FC<Props> = ({ viewStateKey, noWrapper = false }) => {
  const dispatch = useDispatch();
  const storedQuery = useSelector(viewStateSelectors.getQuery(viewStateKey));
  const [localQuery, setLocalQuery] = useState(storedQuery);

  const debouncedDispatch = useRef(
    debounce((query: string) => {
      dispatch(setQuery({ viewStateKey, query }));
    }, config.defaultDebounce || 300),
  ).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalQuery(value);
    debouncedDispatch(value.trim());
  };

  const handleClear = () => {
    setLocalQuery('');
    debouncedDispatch.cancel();
    dispatch(setQuery({ viewStateKey, query: '' }));
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedDispatch.cancel();
    };
  }, [debouncedDispatch]);

  useEffect(() => {
    setLocalQuery(storedQuery);
  }, [storedQuery]);

  const searchInput = (
    <StyledSearchContainer>
      <TextField
        placeholder="Search"
        value={localQuery}
        onChange={handleChange}
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: localQuery ? (
              <InputAdornment position="end">
                <IconButtonRounded
                  icon={<ClearIcon fontSize="small" />}
                  onClick={handleClear}
                  title="Clear search"
                />
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </StyledSearchContainer>
  );

  if (noWrapper) {
    return searchInput;
  }

  return (
    <NgCard>
      <NgCardContent>{searchInput}</NgCardContent>
    </NgCard>
  );
};
